import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

export type ThemeSegmentMode = 'light' | 'dark';

/**
 * The two-position light/dark switch that sits in the account menu's footer slot. It owns no theme
 * service: the host hands in the resolved mode and gets the picked one back, so every product keeps
 * its own theme storage while the control itself stays one piece of markup.
 *
 * The icons are inline SVG because the shared icon set carries no sun/moon glyph.
 */
@Component({
  selector: 'app-theme-segment',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'group',
    'aria-label': 'Тема оформления',
    '[class.is-light]': 'mode() !== "dark"',
  },
  template: `
    <span class="theme-seg__pill" aria-hidden="true"></span>
    <button
      type="button"
      class="theme-seg__btn"
      data-mode="dark"
      aria-label="Тёмная тема"
      [attr.aria-pressed]="mode() === 'dark'"
      (click)="select('dark', $event)"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
      </svg>
    </button>
    <button
      type="button"
      class="theme-seg__btn"
      data-mode="light"
      aria-label="Светлая тема"
      [attr.aria-pressed]="mode() !== 'dark'"
      (click)="select('light', $event)"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="4" />
        <path [attr.d]="sunRaysPath" />
      </svg>
    </button>
  `,
  styles: [
    `
      :host {
        position: relative;
        display: inline-flex;
        flex: 0 0 auto;
        padding: 3px;
        border-radius: var(--r);
        background: var(--bg2);
        border: 1px solid var(--line2);
      }
      .theme-seg__pill {
        position: absolute;
        top: 3px;
        left: 3px;
        width: 30px;
        height: 26px;
        border-radius: var(--r-s);
        background: var(--acc-soft);
        box-shadow: inset 0 0 0 1px var(--acc-line);
        transition: transform var(--t-slow) var(--ease-pop);
      }
      :host(.is-light) .theme-seg__pill {
        transform: translateX(30px);
      }
      .theme-seg__btn {
        position: relative;
        z-index: 1;
        display: inline-grid;
        place-items: center;
        width: 30px;
        height: 26px;
        border: 0;
        background: none;
        cursor: pointer;
        color: var(--c3);
        transition: color var(--t-mid);
      }
      .theme-seg__btn svg {
        width: 15px;
        height: 15px;
        transition: transform var(--t-slow) var(--ease-pop);
      }
      :host(:not(.is-light)) .theme-seg__btn[data-mode='dark'] {
        color: var(--acc-2);
      }
      :host(.is-light) .theme-seg__btn[data-mode='light'] {
        color: var(--acc-2);
      }
      :host(.is-light) .theme-seg__btn[data-mode='dark'] svg {
        transform: rotate(-90deg) scale(0.9);
      }
      :host(:not(.is-light)) .theme-seg__btn[data-mode='light'] svg {
        transform: rotate(90deg) scale(0.9);
      }
      @media (prefers-reduced-motion: reduce) {
        .theme-seg__pill,
        .theme-seg__btn,
        .theme-seg__btn svg {
          transition: none;
        }
      }
    `,
  ],
})
export class ThemeSegmentComponent {
  /** The theme actually in force — the host resolves 'system' before handing it over. */
  readonly mode = input.required<ThemeSegmentMode>();

  /** The photographer picked a mode; the host writes it into its own theme storage. */
  readonly modeSelected = output<ThemeSegmentMode>();

  protected readonly sunRaysPath =
    'M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41';

  // The segment is projected into the menu panel from the host's view, so a bubbling click would
  // reach the panel's own handlers and close the menu under the photographer's finger.
  protected select(mode: ThemeSegmentMode, event: Event): void {
    event.stopPropagation();
    this.modeSelected.emit(mode);
  }
}
